import React, { useRef, useState, useEffect } from 'react';
import JSZip from 'jszip';
import backgroundImage from '../assets/background.jpg';


const Home = () => {
    const [isAuth, setIsAuth]                     = useState(false);
    const [gapiLoaded, setGapiLoaded]             = useState(false);
    const [gapiInitError, setGapiInitError]       = useState('');
    const [authToken, setAuthToken]               = useState('');
    const [tokenExpiry, setTokenExpiry]           = useState(0);
    const [uploadProgress, setUploadProgress]     = useState(0);
    const [isSettingUpEmails, setIsSettingUpEmails] = useState(false);
    const [emailSeriesStatus, setEmailSeriesStatus] = useState('');
    const tokenClientRef = useRef(null);

    // CONFIRMATIONS
    const [xlsxFile, setXlsxFile]                         = useState(null);
    const [confirmationsZip, setConfirmationsZip]         = useState(null);
    const [xlsxFileName, setXlsxFileName]                 = useState("Client Confirmation Excel File");
    const [confirmationsZipName, setConfirmationsZipName] = useState("Confirmation Zip File");

    // CALENDAR
    const [pdfCalendarFile, setPdfFile]       = useState(null);
    const [jpgCalendarFile, setJPGFile]       = useState(null);
    const [odsCalendarFile, setODSFile]       = useState(null);
    const [pdfCalendarName, setCalendarPDFName] = useState("Calendar PDF File");
    const [jpgCalendarName, setCalendarJPGName] = useState("Calendar JPEG File");
    const [odsCalendarName, setCalendarODSName] = useState("Calendar ODS File");

    // BOOKING LIST
    const [pdfBookingListFile, setBLpdfFile] = useState(null);
    const [pdfBookingList, setBLPDFName]     = useState("Booking List PDF File");

    const [isUploading, setIsUploading] = useState(false);

    // Google API credentials
    const CLIENT_ID = '939526187420-cotungv99ei31r3jgdfut3v0718drqk4.apps.googleusercontent.com';
    const API_KEY   = 'AIzaSyAiUUR6IG42uKAtI55lcbrnfPc3FZUdVh0';
    const SCOPES    = "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.labels";

    const MAX_GMAIL_ATTACHMENT_BYTES = 18 * 1024 * 1024;

    // Total number of drafts to create
    const EMAIL_GROUP_COUNT = 8;

    useEffect(() => {
        let gapiInited = false;
        let gisInited  = false;

        const maybeEnableButton = () => {
            if (gapiInited && gisInited) setGapiLoaded(true);
        };

        const initGapiClient = async () => {
            try {
                await window.gapi.client.init({});
                await window.gapi.client.load('https://www.googleapis.com/discovery/v1/apis/drive/v3/rest');
                gapiInited = true;
                maybeEnableButton();
            } catch (error) {
                console.error("Error initializing GAPI client:", error);
                setGapiInitError(`GAPI initialization failed: ${error.message || error}`);
                setGapiLoaded(true);
            }
        };

        const loadGapi = () => { window.gapi.load('client', initGapiClient); };

        const initGis = () => {
            try {
                tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                    client_id: CLIENT_ID,
                    scope: SCOPES,
                    callback: (response) => {
                        if (response && response.access_token) {
                            setAuthToken(response.access_token);
                            setIsAuth(true);
                            setTokenExpiry(Date.now() + 50 * 60 * 1000);
                            setGapiInitError('');
                            if (window.gapi && window.gapi.client) {
                                window.gapi.client.setToken({ access_token: response.access_token });
                            }
                        } else {
                            setGapiInitError('Google sign-in did not return an access token.');
                            setIsAuth(false);
                        }
                    },
                });
                gisInited = true;
                maybeEnableButton();
            } catch (error) {
                console.error("Error initializing GIS:", error);
                setGapiInitError(`GIS initialization failed: ${error.message || error}`);
                setGapiLoaded(true);
            }
        };

        const loadScript = (src, onLoad, onError) => {
            const script = document.createElement('script');
            script.src   = src;
            script.async = true;
            script.defer = true;
            script.onload  = onLoad;
            script.onerror = onError;
            document.body.appendChild(script);
            return script;
        };

        const gapiScript = loadScript(
            "https://apis.google.com/js/api.js", loadGapi,
            () => { setGapiInitError('Failed to load Google API client library.'); setGapiLoaded(true); }
        );
        const gisScript = loadScript(
            "https://accounts.google.com/gsi/client", initGis,
            () => { setGapiInitError('Failed to load Google Identity Services.'); setGapiLoaded(true); }
        );

        return () => {
            if (gapiScript.parentNode) gapiScript.parentNode.removeChild(gapiScript);
            if (gisScript.parentNode)  gisScript.parentNode.removeChild(gisScript);
        };
    }, []);

    const openGmail = () => { window.open('https://gmail.com', '_blank'); };

    // ── Trigger Google sign-in if not already authenticated ───────────────
    // Called automatically when the user clicks Upload or Set Up Email Series
    const ensureAuth = () => {
        return new Promise((resolve, reject) => {
            if (isAuth && authToken && Date.now() < tokenExpiry) {
                // Token still valid — proceed immediately
                resolve(authToken);
                return;
            }
            if (!tokenClientRef.current) {
                reject(new Error('Google Identity Services not initialized yet. Please wait a moment and try again.'));
                return;
            }
            // Temporarily override the callback to resolve this promise
            const originalCallback = tokenClientRef.current.callback;
            tokenClientRef.current.callback = (response) => {
                // Restore original callback
                tokenClientRef.current.callback = originalCallback;
                if (response && response.access_token) {
                    setAuthToken(response.access_token);
                    setIsAuth(true);
                    setTokenExpiry(Date.now() + 50 * 60 * 1000);
                    setGapiInitError('');
                    if (window.gapi && window.gapi.client) {
                        window.gapi.client.setToken({ access_token: response.access_token });
                    }
                    resolve(response.access_token);
                } else {
                    setIsAuth(false);
                    reject(new Error('Google sign-in did not return an access token.'));
                }
            };
            // prompt: '' = silent refresh if still in same Google session;
            // if truly expired Google will show the account picker automatically.
            tokenClientRef.current.requestAccessToken({ prompt: '' });
        });
    };

    // ── Returns "Month YYYY" for NEXT month (used in subject lines) ───────
    const getMonthYear = () => {
        // Try to parse month/year from the loaded filenames first.
        // Filenames follow the pattern: "...Something-Month YYYY V1.ext"
        // e.g. "Exchange Booking Calendar-September 2026 V1.jpg"
        const candidates = [jpgCalendarFile, pdfCalendarFile, odsCalendarFile, pdfBookingListFile];
        const months = [
            'January','February','March','April','May','June',
            'July','August','September','October','November','December'
        ];

        for (const file of candidates) {
            if (!file) continue;
            const name = file.name;
            for (const month of months) {
                // Look for "Month YYYY" pattern anywhere in the filename
                const match = name.match(new RegExp(month + '\\s+(\\d{4})', 'i'));
                if (match) {
                    return `${month} ${match[1]}`;
                }
            }
        }

        // Fallback: use next month from today's date if no filename match found
        const now = new Date();
        now.setMonth(now.getMonth() + 1);
        const month = now.toLocaleString('default', { month: 'long' });
        const year  = now.getFullYear();
        return `${month} ${year}`;
    };

    // ── Convert a File object to a base64 string ──────────────────────────
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = () => {
                const bytes  = new Uint8Array(reader.result);
                let binary   = '';
                for (let i = 0; i < bytes.byteLength; i++) {
                    binary += String.fromCharCode(bytes[i]);
                }
                resolve(btoa(binary));
            };
            reader.onerror = reject;
        });
    };

    // ── Upload a file to Drive and return a shareable link ────────────────
    const createShareableDriveLink = async (file, accessToken) => {
        const attachmentsFolderId = await getFolderId('Gmail Email Series Attachments', accessToken);
        const uploadedFileId      = await uploadFileToDrive(file, accessToken, attachmentsFolderId, 1);

        const permissionResponse = await fetch(
            `https://www.googleapis.com/drive/v3/files/${uploadedFileId}/permissions`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type':  'application/json'
                },
                body: JSON.stringify({ role: 'reader', type: 'anyone' })
            }
        );

        if (!permissionResponse.ok) {
            const err = await permissionResponse.json().catch(() => ({ message: permissionResponse.statusText }));
            throw new Error(`Unable to share ${file.name} from Drive: ${err.error?.message || err.message}`);
        }

        return `https://drive.google.com/file/d/${uploadedFileId}/view?usp=sharing`;
    };

    // ── If a file is too large for Gmail, upload to Drive and add a link ──
    const prepareDraftPayload = async (draftConfig, accessToken, driveLinkCache) => {
        const attachments        = [];
        const bodyLines          = [draftConfig.body];
        const largeAttachmentNotes = [];

        for (const file of draftConfig.attachments) {
            if (!file) continue;
            const estimatedBytes = Math.ceil((file.size * 4) / 3);
            if (estimatedBytes > MAX_GMAIL_ATTACHMENT_BYTES) {
                let driveLink = driveLinkCache.get(file.name);
                if (!driveLink) {
                    driveLink = await createShareableDriveLink(file, accessToken);
                    driveLinkCache.set(file.name, driveLink);
                }
                largeAttachmentNotes.push(`- ${file.name}: ${driveLink}`);
            } else {
                attachments.push(file);
            }
        }

        if (largeAttachmentNotes.length > 0) {
            bodyLines.push('', 'Large files have been uploaded to Google Drive:', ...largeAttachmentNotes);
        }

        return { ...draftConfig, body: bodyLines.join('\n'), attachments };
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  createGmailDraftWithAttachments
    // ─────────────────────────────────────────────────────────────────────────
    const createGmailDraftWithAttachments = async (
        accessToken,
        toAddresses,
        ccAddresses,
        bccAddresses,
        subject,
        body,
        attachmentFiles
    ) => {
        const boundary = '--------' + Math.random().toString(36).substring(2, 15);

        let mimeMessage = '';
        mimeMessage += `To: ${toAddresses.join(', ')}\r\n`;
        if (ccAddresses && ccAddresses.length > 0) {
            mimeMessage += `Cc: ${ccAddresses.join(', ')}\r\n`;
        }
        if (bccAddresses && bccAddresses.length > 0) {
            mimeMessage += `Bcc: ${bccAddresses.join(', ')}\r\n`;
        }
        mimeMessage += `Subject: ${subject}\r\n`;
        mimeMessage += 'MIME-Version: 1.0\r\n';
        mimeMessage += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n`;

        mimeMessage += `--${boundary}\r\n`;
        mimeMessage += 'Content-Type: text/plain; charset="UTF-8"\r\n';
        mimeMessage += 'Content-Transfer-Encoding: 7bit\r\n\r\n';
        mimeMessage += body + '\r\n\r\n';

        for (const file of attachmentFiles) {
            if (file) {
                const base64Content = await fileToBase64(file);
                mimeMessage += `--${boundary}\r\n`;
                mimeMessage += `Content-Type: ${file.type || 'application/octet-stream'}; name="${file.name}"\r\n`;
                mimeMessage += `Content-Disposition: attachment; filename="${file.name}"\r\n`;
                mimeMessage += 'Content-Transfer-Encoding: base64\r\n\r\n';
                mimeMessage += base64Content + '\r\n\r\n';
            }
        }

        mimeMessage += `--${boundary}--`;

        const encoded = window.btoa(unescape(encodeURIComponent(mimeMessage)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');

        const controller     = new AbortController();
        const requestTimeout = setTimeout(() => controller.abort(), 120000);

        try {
            const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/drafts', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type':  'application/json'
                },
                signal: controller.signal,
                body: JSON.stringify({ message: { raw: encoded } })
            });

            if (!response.ok) {
                const errorData    = await response.json().catch(() => ({ message: response.statusText }));
                const errorMessage = errorData.error?.message || errorData.message || response.statusText;
                throw new Error(`Failed to create draft for ${toAddresses[0]}: ${errorMessage}`);
            }
        } catch (fetchError) {
            if (fetchError.name === 'AbortError') {
                throw new Error(`Timed out creating draft for ${toAddresses[0]}. Please try again.`);
            }
            if (fetchError.message.includes('Failed to fetch')) {
                throw new Error(`Network error accessing Gmail API. Check your internet connection.`);
            }
            throw fetchError;
        } finally {
            clearTimeout(requestTimeout);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  getDraftConfigForGroup
    // ─────────────────────────────────────────────────────────────────────────
    const getDraftConfigForGroup = (groupIndex, monthYear, calAttachments, calJpgOnly, bookingListAttach) => {

        const to  = ['musicmattersbookings@gmail.com'];
        const calBody = ``;
        const blBody  = ``;

        switch (groupIndex) {
            case 0:
                return {
                    to,
                    cc:  [
                        'James.Boheim@renaissancemontgomery.com',
                        'Luis.Quezada@renaissancemontgomery.com'
                    ],
                    bcc: [],
                    subject:     `Exchange Booking Calendar-${monthYear} V1`,
                    body:        calBody,
                    attachments: calAttachments
                };
            case 1:
                return {
                    to,
                    cc:  ['Rudi.Williams@renaissancemontgomery.com'],
                    bcc: [],
                    subject:     `Exchange Booking Calendar-Originals-${monthYear} V1`,
                    body:        calBody,
                    attachments: calJpgOnly
                };
            case 2:
                return {
                    to,
                    cc:  [],
                    bcc: [
                        'sheupel@montgome.gannett.com',
                        'sheupel@gannett.com',
                        'sdarnold@montgome.gannett.com',
                        'Montgomeryhotspots@gmail.com'
                    ],
                    subject:     `Renaissance/Exchange Booking Calendar-${monthYear} V1`,
                    body:        calBody,
                    attachments: calJpgOnly
                };
            case 3:
                return {
                    to,
                    cc:  ['ahejazi@parkingmgt.com'],
                    bcc: [],
                    subject:     `Exchange Booking Calendar-${monthYear} V1`,
                    body:        calBody,
                    attachments: calJpgOnly
                };
            case 4:
                return {
                    to,
                    cc:  ['gborland@rtjgolf.com'],
                    bcc: [],
                    subject:     `Exchange Booking Calendar-${monthYear} V1`,
                    body:        ``,
                    attachments: calJpgOnly
                };
            case 5:
                return {
                    to,
                    cc:  [],
                    bcc: [
                        'rick@groundfloorusa.com',
                        'hew2240@aol.com',
                        'segwaymontgomery@gmail.com',
                        'tduvernay@eji.org'
                    ],
                    subject:     `Exchange Booking Calendar-${monthYear} V1`,
                    body:        calBody,
                    attachments: calJpgOnly
                };
            case 6:
                return {
                    to,
                    cc:  [
                        'James.Boheim@renaissancemontgomery.com',
                        'Luis.Quezada@renaissancemontgomery.com'
                    ],
                    bcc: [],
                    subject:     `Exchange Booking List-${monthYear} V1`,
                    body:        blBody,
                    attachments: bookingListAttach
                };
            case 7:
                return {
                    to,
                    cc:  [
                        'Thomas.Lewis@renaissancemontgomery.com',
                        'APMontgomery@renaissancemontgomery.com',
                        'Jared.Clay@renaissancemontgomery.com'
                    ],
                    bcc: [],
                    subject:     `Exchange Booking List-${monthYear} V1`,
                    body:        blBody,
                    attachments: bookingListAttach
                };
            default:
                return {
                    to,
                    cc:  [],
                    bcc: [],
                    subject:     `Exchange Booking Calendar-${monthYear} V1`,
                    body:        calBody,
                    attachments: calJpgOnly
                };
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  handleSetupEmailSeries
    // ─────────────────────────────────────────────────────────────────────────
    const handleSetupEmailSeries = async () => {
        if (!jpgCalendarFile) {
            alert('Please upload the Calendar JPEG file before setting up the email series.');
            return;
        }
        if (!pdfBookingListFile) {
            alert('Please upload the Booking List PDF file before setting up the email series.');
            return;
        }

        setIsSettingUpEmails(true);
        setEmailSeriesStatus('Signing in to Google...');

        try {
            // Sign in automatically if not already authenticated
            const accessToken = await ensureAuth();

            const monthYear      = getMonthYear();
            const driveLinkCache = new Map();

            const calAttachments    = [jpgCalendarFile, pdfCalendarFile, odsCalendarFile].filter(f => f !== null);
            const calJpgOnly        = [jpgCalendarFile].filter(f => f !== null);
            const bookingListAttach = [pdfBookingListFile].filter(f => f !== null);

            let draftsCreated = 0;

            for (let groupIndex = 0; groupIndex < EMAIL_GROUP_COUNT; groupIndex++) {
                const draftConfig = getDraftConfigForGroup(
                    groupIndex,
                    monthYear,
                    calAttachments,
                    calJpgOnly,
                    bookingListAttach
                );

                setEmailSeriesStatus(`Preparing draft ${groupIndex + 1} of ${EMAIL_GROUP_COUNT} — ${draftConfig.subject}...`);

                const preparedDraft = await prepareDraftPayload(draftConfig, accessToken, driveLinkCache);

                setEmailSeriesStatus(`Creating draft ${groupIndex + 1} of ${EMAIL_GROUP_COUNT}...`);

                await createGmailDraftWithAttachments(
                    accessToken,
                    preparedDraft.to,
                    preparedDraft.cc,
                    preparedDraft.bcc,
                    preparedDraft.subject,
                    preparedDraft.body,
                    preparedDraft.attachments
                );

                draftsCreated++;
            }

            setEmailSeriesStatus(`✅ All ${draftsCreated} drafts created successfully! Check your Gmail Drafts folder.`);
            alert(`✅ Created ${draftsCreated} Gmail drafts!\n\nCheck your Gmail Drafts folder. Remember to add your personal note to the Greer Borland draft before sending.`);

        } catch (error) {
            console.error('Error setting up Gmail email series:', error);
            const errorMessage = error?.message || JSON.stringify(error) || 'Unknown error';
            setEmailSeriesStatus(`❌ Email series failed: ${errorMessage}`);
            alert(`Could not set up Gmail email series:\n\n${errorMessage}`);
        } finally {
            setIsSettingUpEmails(false);
        }
    };

    // ── Auth handlers ─────────────────────────────────────────────────────
    const handleSignOutClick = () => {
        if (window.google && window.google.accounts && window.google.accounts.oauth2 && authToken) {
            window.google.accounts.oauth2.revoke(authToken, () => {
                setAuthToken('');
                setIsAuth(false);
                setTokenExpiry(0);
                if (window.gapi && window.gapi.client) {
                    window.gapi.client.setToken(null);
                }
            });
        } else {
            setAuthToken('');
            setIsAuth(false);
            setTokenExpiry(0);
        }
    };

    // ── File input handlers ───────────────────────────────────────────────
    const handleXlsxFileChange = (event) => {
        const file = event.target.files[0];
        setXlsxFile(file);
        setXlsxFileName(file.name);
        document.querySelector("label[for='xlsxFileInput']").classList.add("file-selected");
    };

    const handleConfirmationsZipChange = (event) => {
        const file = event.target.files[0];
        setConfirmationsZip(file);
        setConfirmationsZipName(file.name);
        document.querySelector("label[for='confirmationsZipInput']").classList.add("file-selected");
    };

    const handleBookingListPDFChange = (event) => {
        const file = event.target.files[0];
        setBLpdfFile(file);
        setBLPDFName(file.name);
        document.querySelector("label[for='BLPDFFileInput']").classList.add("file-selected");
    };

    const handleCalendarPDFChange = (event) => {
        const file = event.target.files[0];
        setPdfFile(file);
        setCalendarPDFName(file.name);
        document.querySelector("label[for='CalPDFFileInput']").classList.add("file-selected");
    };

    const handleCalendarJPGChange = (event) => {
        const file = event.target.files[0];
        setJPGFile(file);
        setCalendarJPGName(file.name);
        document.querySelector("label[for='CalJPGFileInput']").classList.add("file-selected");
    };

    const handleCalendarODSChange = (event) => {
        const file = event.target.files[0];
        setODSFile(file);
        setCalendarODSName(file.name);
        document.querySelector("label[for='CalODSFileInput']").classList.add("file-selected");
    };

    // ── Zip extraction ────────────────────────────────────────────────────
    const unzipAndExtractFiles = async (zipFile) => {
        const jszip    = new JSZip();
        const fileData = await zipFile.arrayBuffer();
        const zip      = await jszip.loadAsync(fileData);
        const extractedFiles = [];

        for (const [fileName, file] of Object.entries(zip.files)) {
            if (!file.dir) {
                const blob    = await file.async('blob');
                const newFile = new File([blob], fileName, { type: "application/pdf" });
                extractedFiles.push(newFile);
            }
        }
        return extractedFiles;
    };

    // ── Google Drive helpers ──────────────────────────────────────────────

    // BUG FIX: folder names for Drive uploads were being built from
    // `new Date()` (today's real system date) instead of the actual
    // month/year of the calendar/booking-list content being uploaded.
    // That meant uploading September's files while the system clock was
    // still in July filed them under (or checked them against) a
    // "...-July-2026" folder — so September never got its own folder,
    // and the "already exists" prompt was comparing against July's files,
    // not real September duplicates. Fix: pull month/year from the actual
    // uploaded file's name (matches the standard "Month YYYY" naming
    // convention used everywhere else in the app), falling back to
    // today's date only if no file name matches that pattern.
    const MONTH_NAMES = ['January','February','March','April','May','June','July',
                         'August','September','October','November','December'];
    const extractMonthYearFromFileName = (fileName) => {
        if (!fileName) return null;
        const pattern = new RegExp(`(${MONTH_NAMES.join('|')})\\s+(\\d{4})`, 'i');
        const match = fileName.match(pattern);
        if (!match) return null;
        const properMonth = MONTH_NAMES.find(m => m.toLowerCase() === match[1].toLowerCase());
        return { month: properMonth, year: match[2] };
    };
    const getMonthYearForUpload = (candidateFiles) => {
        for (const file of candidateFiles) {
            const found = file && extractMonthYearFromFileName(file.name);
            if (found) return found;
        }
        // Fallback: no candidate file name matched "Month YYYY" — use
        // today's date and warn, so this is visible if it ever happens
        // instead of silently miscategorizing the upload again.
        console.warn('Upload to Drive: could not determine month/year from any file name — falling back to today\'s date.');
        return {
            month: new Date().toLocaleString('default', { month: 'long' }),
            year:  String(new Date().getFullYear())
        };
    };

    const createFolder = async (folderName, accessToken) => {
        const metadata = {
            name:     folderName,
            mimeType: 'application/vnd.google-apps.folder'
        };
        const response = await fetch('https://www.googleapis.com/drive/v3/files', {
            method:  'POST',
            headers: { 'Authorization': 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
            body:    JSON.stringify(metadata)
        });
        const folder = await response.json();
        if (!response.ok || folder.error || !folder.id) {
            const msg = folder.error?.message || response.statusText || 'Unknown Drive API error';
            throw new Error(`Google Drive error while creating folder "${folderName}": ${msg}`);
        }
        return folder.id;
    };

    const getFolderId = async (folderName, accessToken) => {
        const query    = `name = '${folderName}' and mimeType = 'application/vnd.google-apps.folder'`;
        const response = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}`, {
            headers: { 'Authorization': 'Bearer ' + accessToken }
        });
        const result = await response.json();
        if (!response.ok || result.error) {
            const msg = result.error?.message || response.statusText || 'Unknown Drive API error';
            throw new Error(`Google Drive error while looking up folder "${folderName}": ${msg}`);
        }
        return result.files.length > 0
            ? result.files[0].id
            : await createFolder(folderName, accessToken);
    };

    const uploadFileToDrive = async (file, accessToken, folderId, totalFiles) => {
        let uploadedFiles = 0;
        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify({
            name:     file.name,
            mimeType: file.type,
            parents:  [folderId]
        })], { type: 'application/json' }));
        form.append('file', file);

        const responseText = await fetchWithProgress(
            'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
            { method: 'POST', headers: { 'Authorization': 'Bearer ' + accessToken }, body: form },
            (progress) => {
                const aggregatedProgress = (uploadedFiles + progress / totalFiles) * 100;
                setUploadProgress(aggregatedProgress);
            }
        );

        let data;
        try {
            data = JSON.parse(responseText);
        } catch (parseErr) {
            throw new Error(`Google Drive returned an unexpected response while uploading "${file.name}". It may need re-authorization.`);
        }
        if (data && data.id) {
            setUploadProgress(100);
            return data.id;
        } else {
            const msg = data?.error?.message || 'Unknown error';
            throw new Error(`Error uploading "${file.name}" to Google Drive: ${msg}`);
        }
    };

    const findExistingFileIds = async (fileName, folderId, accessToken) => {
        try {
            const baseFileName = fileName.replace(/\.[^/.]+$/, "");
            const safeFileName = encodeURIComponent(baseFileName);
            const query        = `name contains '${safeFileName}' and '${folderId}' in parents`;
            const response     = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${query}`,
                { headers: { 'Authorization': 'Bearer ' + accessToken } }
            );
            if (!response.ok) throw new Error(`Error in Google Drive API: ${response.statusText}`);
            const result = await response.json();
            return result.files.map(f => f.id);
        } catch (error) {
            console.error("Error checking if file exists in Google Drive:", error);
            return [];
        }
    };

    const deleteFileFromDrive = async (fileId, accessToken) => {
        const response = await fetch(
            `https://www.googleapis.com/drive/v3/files/${fileId}`,
            { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + accessToken } }
        );
        if (!response.ok && response.status !== 204) {
            throw new Error(`Failed to delete existing file (id: ${fileId}): ${response.statusText}`);
        }
    };

    const handleDedicatedUpload = async (accessToken) => {
        if (!xlsxFile && !confirmationsZip) return;

        const { month, year } = getMonthYearForUpload([xlsxFile]);
        const xlsxFolderName          = `ExcelFiles-${month}-${year}`;
        const confirmationsFolderName = `Confirmations-${month}-${year}`;

        const xlsxFolderId          = await getFolderId(xlsxFolderName, accessToken);
        const confirmationsFolderId = await getFolderId(confirmationsFolderName, accessToken);

        // ── Excel file: check for existing duplicate, offer overwrite or skip ──
        let uploadXlsx = !!xlsxFile;
        if (xlsxFile) {
            const existingXlsxIds = await findExistingFileIds(xlsxFile.name, xlsxFolderId, accessToken);
            if (existingXlsxIds.length > 0) {
                const overwrite = window.confirm(
                    `"${xlsxFile.name}" already exists in Drive.\n\n` +
                    `Click OK to DELETE the existing file and replace it with this new version.\n` +
                    `Click Cancel to skip uploading this file (keep the old one).`
                );
                if (overwrite) {
                    for (const id of existingXlsxIds) await deleteFileFromDrive(id, accessToken);
                } else {
                    uploadXlsx = false;
                }
            }
        }

        const confirmationFiles = confirmationsZip ? await unzipAndExtractFiles(confirmationsZip) : [];

        // ── Confirmation PDFs: check each one individually, offer overwrite or skip ──
        const filesToUpload = [];
        for (const file of confirmationFiles) {
            const existingIds = await findExistingFileIds(file.name, confirmationsFolderId, accessToken);
            if (existingIds.length > 0) {
                const overwrite = window.confirm(
                    `"${file.name}" already exists in Drive.\n\n` +
                    `Click OK to DELETE the existing file and replace it with this new version.\n` +
                    `Click Cancel to skip uploading this file (keep the old one).`
                );
                if (overwrite) {
                    for (const id of existingIds) await deleteFileFromDrive(id, accessToken);
                    filesToUpload.push(file);
                }
            } else {
                filesToUpload.push(file);
            }
        }

        const totalFiles = (uploadXlsx ? 1 : 0) + filesToUpload.length;
        if (totalFiles === 0) return;

        let uploadedFiles = 0;
        if (uploadXlsx) {
            await convertAndUpload(xlsxFile, accessToken, xlsxFolderId);
            uploadedFiles++;
            setUploadProgress((uploadedFiles / totalFiles) * 100);
        }
        for (const file of filesToUpload) {
            await uploadFileToDrive(file, accessToken, confirmationsFolderId);
            uploadedFiles++;
            setUploadProgress((uploadedFiles / totalFiles) * 100);
        }
    };

    const handleCal_BL_Upload = async (accessToken) => {
        const calendarCandidates    = [pdfCalendarFile, jpgCalendarFile, odsCalendarFile].filter(f => f);
        const bookingListCandidates = pdfBookingListFile ? [pdfBookingListFile] : [];
        if (calendarCandidates.length === 0 && bookingListCandidates.length === 0) return 0;

        const { month, year } = getMonthYearForUpload([...calendarCandidates, ...bookingListCandidates]);
        const bookingListFolderName = `BookingList-${month}-${year}`;
        const calendarFolderName    = `ExchangeBookingCalendar-${month}-${year}`;

        const bookingListFolderId = await getFolderId(bookingListFolderName, accessToken);
        const calendarFolderId    = await getFolderId(calendarFolderName, accessToken);

        const filesToUpload = [];

        const checkAndQueue = async (file, folderId) => {
            const existingIds = await findExistingFileIds(file.name, folderId, accessToken);
            if (existingIds.length > 0) {
                const overwrite = window.confirm(
                    `"${file.name}" already exists in Drive.\n\n` +
                    `Click OK to DELETE the existing file and replace it with this new version.\n` +
                    `Click Cancel to skip uploading this file (keep the old one).`
                );
                if (overwrite) {
                    for (const id of existingIds) await deleteFileFromDrive(id, accessToken);
                    filesToUpload.push({ file, folderId });
                }
            } else {
                filesToUpload.push({ file, folderId });
            }
        };

        for (const file of bookingListCandidates) await checkAndQueue(file, bookingListFolderId);
        for (const file of calendarCandidates)    await checkAndQueue(file, calendarFolderId);

        let uploadedFiles = 0;
        for (const { file, folderId } of filesToUpload) {
            await uploadFileToDrive(file, accessToken, folderId);
            uploadedFiles++;
            setUploadProgress((uploadedFiles / filesToUpload.length) * 100);
        }
        return uploadedFiles;
    };

    const convertAndUpload = async (file, accessToken, folderId) => {
        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify({
            name:     file.name,
            mimeType: 'application/vnd.google-apps.spreadsheet',
            parents:  [folderId]
        })], { type: 'application/json' }));
        form.append('file', file);

        await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
            method:  'POST',
            headers: { 'Authorization': 'Bearer ' + accessToken },
            body:    form
        });
    };

    const fetchWithProgress = (url, options, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(options.method || 'GET', url);
            for (let header in options.headers) {
                xhr.setRequestHeader(header, options.headers[header]);
            }
            xhr.onload  = e => resolve(e.target.responseText);
            xhr.onerror = reject;
            if (xhr.upload && onProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) onProgress(event.loaded / event.total);
                };
            }
            xhr.send(options.body);
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  GMAIL LABEL SETUP
    //
    //  Creates the full Exchange label hierarchy for a new year:
    //
    //  Exchange YYYY  (parent)
    //  ├── Exchange Artist Bookings YYYY         → 12 monthly sub-labels
    //  ├── Exchange Artist Booking List YYYY     → 12 monthly sub-labels
    //  ├── Exchange Artist Calendar YYYY         → 12 monthly sub-labels
    //  ├── Exchange Artist Confirmations YYYY    → 12 monthly sub-labels
    //  ├── Exchange Artist Invoices YYYY         → 12 monthly sub-labels
    //  ├── Exchange Artist Misc YYYY             → no monthly sub-labels
    //  └── Feb 10th Emails YYYY                 → no monthly sub-labels
    //
    //  Total: 1 parent + 7 sub-labels + 60 monthly sub-labels = 68 labels
    // ─────────────────────────────────────────────────────────────────────────

    const [isSettingUpLabels, setIsSettingUpLabels] = useState(false);
    const [labelSetupStatus, setLabelSetupStatus]   = useState('');

    const MONTHS = [
        'January','February','March','April','May','June',
        'July','August','September','October','November','December'
    ];

    const SUB_CATEGORIES_WITH_MONTHS = [
        'Exchange Artist Bookings',
        'Exchange Artist Booking List',
        'Exchange Artist Calendar',
        'Exchange Artist Confirmations',
        'Exchange Artist Invoices',
    ];

    const SUB_CATEGORIES_NO_MONTHS = [
        'Exchange Artist Misc',
        'Feb 10th Emails',
    ];

    // ── Create a single Gmail label, returns its ID ───────────────────────
    const createGmailLabel = async (accessToken, labelName, parentId = null) => {
        const body = { name: labelName, labelListVisibility: 'labelShow', messageListVisibility: 'show' };
        if (parentId) body.parentId = parentId;

        const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
            method:  'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type':  'application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({ message: response.statusText }));
            // If label already exists (409) just fetch its ID instead of failing
            if (response.status === 409) {
                return await getGmailLabelId(accessToken, labelName);
            }
            throw new Error(`Failed to create label "${labelName}": ${err.error?.message || err.message}`);
        }

        const data = await response.json();
        return data.id;
    };

    // ── Get ID of an existing Gmail label by name ─────────────────────────
    const getGmailLabelId = async (accessToken, labelName) => {
        const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const data = await response.json();
        const match = (data.labels || []).find(l => l.name === labelName);
        return match ? match.id : null;
    };

    // ── Main handler: creates all labels for the new year ─────────────────
    const handleSetupGmailLabels = async () => {
        // Figure out next year automatically
        const nextYear = new Date().getFullYear() + 1;

        const confirmed = window.confirm(
            `This will create all Gmail labels for Exchange ${nextYear}.\n\n` +
            `That's 68 labels total:\n` +
            `• 1 parent label: Exchange ${nextYear}\n` +
            `• 5 category labels with 12 monthly sub-labels each (60 total)\n` +
            `• 2 category labels without monthly sub-labels\n\n` +
            `Any labels that already exist will be skipped.\n\n` +
            `Click OK to proceed.`
        );

        if (!confirmed) return;

        setIsSettingUpLabels(true);
        setLabelSetupStatus(`Setting up Gmail labels for Exchange ${nextYear}...`);

        try {
            const accessToken = await ensureAuth();

            let createdCount = 0;
            let skippedCount = 0;

            // ── 1. Create parent label: "Exchange YYYY" ───────────────────
            setLabelSetupStatus(`Creating parent label: Exchange ${nextYear}...`);
            const parentName = `Exchange ${nextYear}`;
            const parentId   = await createGmailLabel(accessToken, parentName);
            if (parentId) createdCount++; else skippedCount++;

            // ── 2. Create sub-categories WITH monthly sub-labels ──────────
            for (const category of SUB_CATEGORIES_WITH_MONTHS) {
                const categoryName = `${category} ${nextYear}`;
                setLabelSetupStatus(`Creating: ${categoryName}...`);
                const categoryId = await createGmailLabel(accessToken, categoryName, parentId);
                if (categoryId) createdCount++; else skippedCount++;

                // Create 12 monthly sub-labels under this category
                for (const month of MONTHS) {
                    const monthLabelName = `${categoryName}-${month}`;
                    setLabelSetupStatus(`Creating: ${monthLabelName}...`);
                    const monthId = await createGmailLabel(accessToken, monthLabelName, categoryId);
                    if (monthId) createdCount++; else skippedCount++;
                }
            }

            // ── 3. Create sub-categories WITHOUT monthly sub-labels ───────
            for (const category of SUB_CATEGORIES_NO_MONTHS) {
                const categoryName = `${category} ${nextYear}`;
                setLabelSetupStatus(`Creating: ${categoryName}...`);
                const categoryId = await createGmailLabel(accessToken, categoryName, parentId);
                if (categoryId) createdCount++; else skippedCount++;
            }

            const statusMsg = `✅ Exchange ${nextYear} labels created! ${createdCount} labels created, ${skippedCount} already existed.`;
            setLabelSetupStatus(statusMsg);
            alert(`✅ Gmail labels for Exchange ${nextYear} are ready!\n\n${createdCount} labels created.\n${skippedCount} labels already existed and were skipped.\n\nCheck Gmail to see your new label structure.`);

        } catch (error) {
            console.error('Error setting up Gmail labels:', error);
            const errorMessage = error?.message || 'Unknown error';
            setLabelSetupStatus(`❌ Label setup failed: ${errorMessage}`);
            alert(`Could not set up Gmail labels:\n\n${errorMessage}`);
        } finally {
            setIsSettingUpLabels(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  RENDER — page loads directly, no sign-in gate
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className='home' style={{
            backgroundImage:    `url(${backgroundImage})`,
            backgroundSize:     'cover',
            backgroundPosition: 'center',
            backgroundRepeat:   'no-repeat',
            height:             '100vh',
        }}>
            <center style={{ paddingTop: '100px' }}>
                <h1>Welcome to the Music Matters Booking System</h1>
                {gapiInitError && <p style={{ color: '#b00020', fontWeight: 'bold' }}>{gapiInitError}</p>}

                <div className="inputs-container" style={{ paddingTop: '150px' }}>
                    <h3>Upload Files Here</h3>

                    {/* ── Calendar files row ── */}
                    <div style={{ paddingTop: '25px' }} className="input-wrapper">
                        <input type="file" id="CalJPGFileInput" className="custom-file-input" accept=".jpg,.jpeg" onChange={handleCalendarJPGChange} />
                        <label htmlFor="CalJPGFileInput" className="custom-file-label">{jpgCalendarName}</label>

                        <div style={{ paddingRight: '25px' }} className="input-wrapper"></div>
                        <input type="file" id="CalODSFileInput" className="custom-file-input" accept=".ods" onChange={handleCalendarODSChange} />
                        <label htmlFor="CalODSFileInput" className="custom-file-label">{odsCalendarName}</label>

                        <div style={{ paddingRight: '25px' }} className="input-wrapper"></div>
                        <input type="file" id="CalPDFFileInput" className="custom-file-input" accept=".pdf" onChange={handleCalendarPDFChange} />
                        <label htmlFor="CalPDFFileInput" className="custom-file-label">{pdfCalendarName}</label>
                    </div>

                    {/* ── Booking list + confirmations row ── */}
                    <div style={{ paddingTop: '25px' }} className="input-wrapper">
                        <input type="file" id="BLPDFFileInput" className="custom-file-input" accept=".pdf" onChange={handleBookingListPDFChange} />
                        <label htmlFor="BLPDFFileInput" className="custom-file-label">{pdfBookingList}</label>

                        <div style={{ paddingRight: '25px' }} className="input-wrapper"></div>
                        <input type="file" id="xlsxFileInput" className="custom-file-input" accept=".xlsx" onChange={handleXlsxFileChange} />
                        <label htmlFor="xlsxFileInput" className="custom-file-label">{xlsxFileName}</label>

                        <div style={{ paddingRight: '25px' }} className="input-wrapper"></div>
                        <input type="file" id="confirmationsZipInput" className="custom-file-input" accept=".zip" onChange={handleConfirmationsZipChange} />
                        <label htmlFor="confirmationsZipInput" className="custom-file-label">{confirmationsZipName}</label>
                    </div>

                    {/* ── Action buttons ── */}
                    <div className="button-group">
                        <button className="button" onClick={async () => {
                            const hasConfirmations = xlsxFile || confirmationsZip;
                            const hasCalBL = pdfCalendarFile || jpgCalendarFile || odsCalendarFile || pdfBookingListFile;

                            if (!hasConfirmations && !hasCalBL) {
                                alert('No files selected. Please choose files to upload first.');
                                return;
                            }

                            setIsUploading(true);
                            let accessToken;
                            try {
                                accessToken = await ensureAuth();
                            } catch (err) {
                                alert('Google sign-in failed: ' + err.message);
                                setIsUploading(false);
                                return;
                            }

                            try {
                                let totalUploaded = 0;
                                let totalSkipped  = 0;

                                if (hasConfirmations) {
                                    await handleDedicatedUpload(accessToken);
                                }
                                if (hasCalBL) {
                                    const calUploaded = await handleCal_BL_Upload(accessToken);
                                    totalUploaded += calUploaded || 0;
                                }

                                alert('Upload Complete. Files are now ready to be sent out!');
                            } catch (err) {
                                console.error('Upload to Drive failed:', err);
                                alert(
                                    'Upload to Drive failed: ' + (err.message || 'Unknown error') +
                                    '\n\nThis is usually caused by an expired or insufficient Google Drive permission. ' +
                                    'Try clicking "Sign Out" then "Upload to Drive" again to re-authorize.'
                                );
                            } finally {
                                setIsUploading(false);
                            }
                        }}>
                            Upload to Drive
                        </button>
                        {isAuth && (
                            <button className="button" onClick={handleSignOutClick}>
                                Sign Out
                            </button>
                        )}
                        <button className="button" onClick={openGmail}>
                            Open Gmail
                        </button>
                        <button
                            className="button"
                            onClick={handleSetupEmailSeries}
                            disabled={isSettingUpEmails}
                        >
                            {isSettingUpEmails ? 'Creating Drafts...' : 'Set Up Email Series (10th)'}
                        </button>
                    </div>

                    {/* ── Status message — email series ── */}
                    {emailSeriesStatus && (
                        <p style={{ marginTop: '12px', fontWeight: 'bold' }}>
                            {emailSeriesStatus}
                        </p>
                    )}

                    {isUploading && <p style={{ marginTop: '20px' }}>Upload in Progress...</p>}

                    {/* ── Gmail Label Setup section ── */}
                    <div style={{ marginTop: '30px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '20px' }}>
                        <h3>New Year Gmail Label Setup</h3>
                        <p style={{ marginTop: '8px', marginBottom: '16px', fontSize: '0.9rem' }}>
                            Use this each fall to automatically create all Exchange Gmail labels for the upcoming year.
                        </p>
                        <div className="button-group">
                            <button
                                className="button"
                                onClick={handleSetupGmailLabels}
                                disabled={isSettingUpLabels}
                            >
                                {isSettingUpLabels ? 'Creating Labels...' : `Setup Gmail Labels (Exchange ${new Date().getFullYear() + 1})`}
                            </button>
                        </div>
                        {/* ── Status message — label setup ── */}
                        {labelSetupStatus && (
                            <p style={{ marginTop: '12px', fontWeight: 'bold' }}>
                                {labelSetupStatus}
                            </p>
                        )}
                    </div>
                </div>
            </center>

            {/* ── Upload progress bar ── */}
            <div className="progress-bar" style={{
                position:  'absolute',
                bottom:    '0',
                left:      '50%',
                transform: 'translateX(-50%)',
                height:    '10px',
                transition: 'width 0.5s ease-in-out'
            }}>
                <div className="progress" style={{ width: `${uploadProgress}%` }}></div>
            </div>
        </div>
    );
};

export default Home;
